"use client";

import { useState } from "react";
import { X, Ticket, AlertCircle, CheckCircle, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBooking } from "@/contexts/BookingContext";
import { BookingStep, PackageVoucher, Service } from "@/types/booking";
import { useRedeemVoucher } from "@/lib/apollo/hooks/booking-edge";
import { usePreserveQueryParams } from "@/hooks/usePreserveQueryParams";

export default function VoucherRedemptionModal() {
  const { navigateWithParams } = usePreserveQueryParams();
  const {
    openVoucherRedemptionModal,
    setOpenVoucherRedemptionModal,
    salons,
    selectedSalon,
    setVoucherToBeRedeemed,
    formattedPackages,
    addToast,
    selectService,
    selectedServices,
    handleNextStep,
    setServiceClient,
    businessInfo,
  } = useBooking();

  const [redeemVoucher] = useRedeemVoucher();
  const [voucherCode, setVoucherCode] = useState("");
  const [voucher, setVoucher] = useState<PackageVoucher | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [selectVoucherServices, setSelectVoucherServices] = useState(false);
  const [voucherServices, setVoucherServices] = useState<Service[]>([]);

  if (!openVoucherRedemptionModal) return null;

  const isVoucherValid = () => {
    const selectedSalonId = selectedSalon?.id;
    const voucherSalonId = voucher?.salonId;
    if (selectedSalonId && voucherSalonId) {
      return selectedSalonId === voucherSalonId;
    }
    return false;
  };

  const voucherSalon = () => {
    if (voucher?.salonId) {
      return salons.find((salon) => salon.id === voucher.salonId);
    }
    return null;
  };

  const incrementQuantity = (serviceId: string) => {
    setVoucherServices((prev) =>
      prev.map((service) => {
        if (service.id === serviceId) {
          const voucherServiceData = service.voucherServiceData;
          const maxQuantity = voucherServiceData?.quantity || 1;
          if (maxQuantity) {
            addToast({
              message: `You cannot redeem more than ${maxQuantity} of this service`,
              variant: "warning",
            });
            return service;
          }
          const currentQuantity = service.quantity || 0;
          return { ...service, quantity: currentQuantity + 1 };
        }
        return service;
      })
    );
  };

  const decrementQuantity = (serviceId: string) => {
    setVoucherServices((prev) =>
      prev.map((service) =>
        service.id === serviceId
          ? { ...service, quantity: Math.max(0, (service.quantity || 0) - 1) }
          : service
      )
    );
  };

  const handleClearVoucher = ({ showToast = true }) => {
    setVoucherToBeRedeemed(null);
    setVoucherServices([]);
    setVoucherCode("");
    setVoucher(null);
    setOpenVoucherRedemptionModal(false);
    if (showToast) {
      addToast({
        message: "Voucher removed",
        variant: "warning",
      });
    }
  };

  const handleValidateVoucher = async () => {
    if (!voucherCode.trim()) {
      addToast({
        message: "Please enter a voucher code",
        variant: "error",
      });
      return;
    }

    setIsValidating(true);
    setValidationError("");

    try {
      const response = await redeemVoucher({
        input: { voucherCode: voucherCode.trim() },
      });
      const result = response?.data?.redeemVoucherCode;
      if (result.errors && result.errors.length > 0) {
        setValidationError(result.errors[0].message);
        return;
      }
      setVoucher(result.voucher);
      const salonServices = selectedSalon?.services || [];

      const voucherServicesData = result.voucher.voucherServices || [];
      const populateServices: Service[] = voucherServicesData
        .filter((voucherService: any) => {
          return (
            !voucherService.isRedeemed &&
            salonServices.some(
              (salonService) =>
                salonService.id ===
                (voucherService.serviceId || voucherService.service?.id)
            )
          );
        })
        .map((voucherService: any) => {
          const salonService = salonServices.find((s) => {
            return (
              s.id === (voucherService.serviceId || voucherService.service?.id)
            );
          });
          return {
            ...salonService,
            ...voucherService.service,
            quantity: 1,
            voucherServiceData: voucherService,
          };
        });

      setVoucherServices(populateServices);
      addToast({
        message: `Voucher "${voucherCode}" validated successfully!`,
        variant: "success",
      });
      if (
        (result.voucher.voucherServices &&
          result.voucher.voucherServices.length > 0) ||
        (result.voucher.services && result.voucher.services.length > 0)
      ) {
        setSelectVoucherServices(true);
      }
    } catch (error: any) {
      setValidationError(error?.message || "Invalid or expired voucher code");
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => setOpenVoucherRedemptionModal(false)}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full max-h-[70vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-grey-100 flex-shrink-0">
          <h2 className="text-xl font-semibold text-grey-900">
            Redeem Your Voucher
          </h2>
          <button
            onClick={() => {
              setOpenVoucherRedemptionModal(false);
              setVoucher(null);
              setVoucherCode("");
              setVoucherServices([]);
            }}
            className="p-2 hover:bg-grey-50 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-grey-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Show current voucher if one is already selected */}
          {voucher ? (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <p className="text-grey-900 font-medium">Voucher Applied</p>
                <p className="text-grey-600 mt-1">Code: {voucher.code}</p>
              </div>

              {voucherServices && voucherServices.length > 0 && (
                <div className="mb-10">
                  {voucherServices.map(function (service) {
                    const isSelected = selectedServices.some(
                      (s) => s.id === service.id
                    );
                    return (
                      <div
                        key={service.id}
                        className=" border border-grey-100 rounded-sm p-4 mb-4"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="text-base font-semibold text-grey-900 mb-1 font-heading">
                              {service.name}
                            </h3>

                            <div
                              onClick={function () {
                                selectService(service.id);
                              }}
                              className="flex items-center gap-2 text-sm"
                            >
                              <p
                                className={`text-green-300 underline cursor-pointer ${
                                  isSelected ? "text-red-500" : "text-green-300"
                                }`}
                              >
                                {isSelected ? "Remove" : "Redeem service"}
                              </p>
                            </div>
                          </div>

                          {/* Quantity Selector */}
                          {isSelected && (
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => decrementQuantity(service.id)}
                                className="w-8 h-8 rounded-full border border-grey-200 flex items-center justify-center hover:bg-grey-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={(service.quantity || 0) <= 0}
                              >
                                <Minus className="h-3 w-3 text-grey-600" />
                              </button>
                              <span className="w-8 text-center text-sm font-medium text-grey-900">
                                {service.quantity || 0}
                              </span>
                              <button
                                onClick={() => incrementQuantity(service.id)}
                                className="w-8 h-8 rounded-full border border-grey-200 flex items-center justify-center hover:bg-grey-50 transition-colors"
                              >
                                <Plus className="h-3 w-3 text-grey-600" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {!isVoucherValid() && (
                <div>
                  <p className="text-sm text-grey-600">
                    This voucher is valid for{" "}
                    <span className="font-semibold text-green-300">
                      {voucherSalon()?.branchName}
                    </span>
                  </p>
                </div>
              )}

              {isVoucherValid() && (
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleClearVoucher({ showToast: true })}
                    variant="outline"
                    className="flex-1"
                  >
                    Remove Voucher
                  </Button>
                  <Button
                    onClick={() => {
                      if (selectVoucherServices) {
                        handleClearVoucher({ showToast: false });
                        setVoucherToBeRedeemed(voucher);
                        setServiceClient(voucher.client || null);
                        handleNextStep(BookingStep.STAFF_SELECTION);
                      }
                    }}
                    className="flex-1 bg-green-300 hover:bg-green-400 text-white"
                  >
                    Continue
                  </Button>
                </div>
              )}
              {!isVoucherValid() && (
                <div className="w-full">
                  <Button
                    onClick={() => {
                      // Navigate to the new salon URL
                      const businessUrl =
                        businessInfo?.bookingUrlIdentifier ||
                        businessInfo?.urlIdentifier;
                      const salonUrl =
                        voucherSalon()?.urlIdentifier || voucherSalon()?.id;
                      navigateWithParams(`/${businessUrl}/${salonUrl}`);
                    }}
                    className="flex-1 bg-green-300 hover:bg-green-400 text-white"
                  >
                    Switch to {voucherSalon()?.branchName}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center mb-4">
                  <Ticket className="h-8 w-8 text-green-300" />
                </div>
                <p className="text-grey-600">
                  Enter your voucher code to redeem services
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="voucher-code"
                    className="block text-sm font-medium text-grey-700 mb-2"
                  >
                    Voucher Code
                  </label>
                  <input
                    id="voucher-code"
                    type="text"
                    value={voucherCode}
                    onChange={(e) => {
                      setVoucherCode(e.target.value.toUpperCase());
                      setValidationError("");
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleValidateVoucher();
                      }
                    }}
                    placeholder="Enter code (e.g., GIFT2024)"
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-grey-900 uppercase ${
                      validationError
                        ? "border-red-500 focus:ring-red-300"
                        : "border-grey-200 focus:ring-green-300"
                    }`}
                    disabled={isValidating}
                  />
                  {validationError && (
                    <div className="mt-2 flex items-start space-x-2">
                      <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-red-500">{validationError}</p>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleValidateVoucher}
                  disabled={isValidating || !voucherCode.trim()}
                  className="w-full bg-green-300 hover:bg-green-400 text-white h-12"
                >
                  {isValidating ? "Validating..." : "Validate & Continue"}
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t border-grey-100">
                <p className="text-xs text-grey-500 text-center">
                  Voucher codes are case-insensitive and typically consist of
                  letters and numbers. If you're having trouble, please contact
                  support.
                </p>

                {formattedPackages?.length > 0 && (
                  <div className="mt-4 p-3 bg-grey-50 rounded-lg">
                    <p className="text-xs text-grey-600 text-center">
                      {formattedPackages.length} package
                      {formattedPackages.length > 1 ? "s" : ""} available for
                      redemption
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
